<script module lang="ts">
  import { I18n, i18n } from '#user/lib/i18n'

  export const displayOptions = [
    { value: 'grid', icon: 'th-large', text: I18n('grid') },
    { value: 'list', icon: 'align-justify', text: I18n('list') },
  ] as const

  export type SubEntitiesBrowserDisplayMode = typeof displayOptions[number]['value']
</script>
<script lang="ts">
  import { property, map } from 'lodash-es'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { getLocalStorageStore } from '#app/lib/components/stores/local_storage_stores'
  import { screen } from '#app/lib/components/stores/screen'
  import { icon } from '#app/lib/icons'
  import type { Facets, FacetsSelectedValues, TextFilterUris } from '#app/types/entity'
  import SelectDropdown from '#components/select_dropdown.svelte'
  import SubEntitiesBrowserFacets from '#entities/components/layouts/sub_entities_browser_facets.svelte'
  import SubEntitiesBrowserSection from '#entities/components/layouts/sub_entities_browser_section.svelte'
  import SubEntitiesBrowserTextFilter from '#entities/components/layouts/sub_entities_browser_text_filter.svelte'
  import type { FacetsSelectors } from '#entities/components/lib/sub_entities_browser_helpers'
  import type { SubEntitiesBrowserSectionData } from '../lib/entities'

  const {
    sections = [],
  }: {
    sections?: SubEntitiesBrowserSectionData[]
  } = $props()

  const allSubEntities = $derived(map(sections, 'entities').flat())

  const displayMode = getLocalStorageStore('entitiesDisplay', 'grid')
  let flash: FlashState = $state()
  let facets: Facets = $state()
  let facetsSelectors: FacetsSelectors = $state()
  let facetsSelectedValues: FacetsSelectedValues = $state()
  let textFilterUris: TextFilterUris = $state()

  let wrapped = $state(true)
  const smallScreenThreshold = 1000
  const isNotEmpty = $derived(sections.map(property('uris')).flat().length > 0)

  const showControls = $derived($screen.isLargerThan(smallScreenThreshold - 1) || !wrapped)
</script>

<Flash state={flash} />
<div class="sub-entities-browser">
  {#if isNotEmpty}
    <div
      class="wrapper"
      class:unwrapped={showControls}
    >
      {#if showControls}
        {#if isNonEmptyArray(allSubEntities)}
          <div class="controls">
            <SubEntitiesBrowserFacets
              subEntities={allSubEntities}
              bind:facets
              bind:facetsSelectors
              bind:facetsSelectedValues
              bind:flash
            />
            <SubEntitiesBrowserTextFilter bind:textFilterUris />
            <SelectDropdown
              bind:value={$displayMode}
              options={displayOptions}
              buttonLabel={I18n('display_mode')} />
          </div>
        {/if}
      {/if}
      {#if $screen.isSmallerThan(smallScreenThreshold) && isNotEmpty}
        <button
          class="toggle-controls"
          onclick={() => wrapped = !wrapped}
          aria-controls="sub-entities-browser-controls"
        >
          {#if showControls}
            {@html icon('caret-up')}
          {:else}
            {@html icon('cog')}
            {i18n('Advanced options')}
            {@html icon('caret-down')}
          {/if}
        </button>
      {/if}
    </div>
  {/if}

  {#if sections}
    {#each sections as section (section.key)}
      <SubEntitiesBrowserSection
        {section}
        displayMode={$displayMode}
        {facets}
        {facetsSelectedValues}
        {textFilterUris}
      />
    {/each}
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .controls{
    margin-block-end: 0.5em;
    @include radius;
    padding: 0.5em;
    @include display-flex(row, center, flex-start, wrap);
    flex: 1;
    :global(.select-dropdown), :global(.dropdown-content){
      inline-size: 10em;
    }
    :global(.select-dropdown), :global(.sub-entities-browser-text-filter){
      margin: 0.5em;
    }
  }

  .wrapper{
    @include display-flex(row);
    margin-block: 0.5em;
    &:not(.unwrapped){
      @include display-flex(column, flex-end);
    }
    &.unwrapped{
      background-color: $off-white;
      .toggle-controls{
        align-self: flex-start;
      }
    }
  }
  .toggle-controls{
    z-index: 10;
    @include display-flex(row, flex-start, center);
    @include bg-hover($off-white);
    @include radius;
    padding: 0.5em;
    color: $grey;
    :global(.fa){
      font-size: 1.4rem;
      padding: 0;
      margin: 0;
    }
  }

  /* Large screens */
  @media screen and (width >= $small-screen){
    .controls{
      position: sticky;
      inset-block-start: $topbar-height;
      inset-inline: 0;
      z-index: 1;
    }
  }

  /* Small screens */
  @media screen and (width < $small-screen){
    .controls{
      :global(.select-dropdown), :global(.sub-entities-browser-text-filter){
        margin: 0.5em auto;
      }
    }
  }

  /* Smaller screens */
  @media screen and (width < 450px){
    .controls{
      :global(.select-dropdown), :global(.dropdown-content), :global(.sub-entities-browser-text-filter){
        margin: 0.5em;
        width: 100%;
      }
    }
    .wrapper{
      &:not(.unwrapped){
        @include display-flex(column, center, stretch);
      }
      @include display-flex(column, center, space-between);
      &.unwrapped{
        .toggle-controls{
          align-self: stretch;
        }
      }
    }
  }
</style>
